import React, { useEffect, useState } from "react";
import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonInput from "../input";
import CommonButton from "../button";
import { serverFieldError } from "./utils";

const useStyles = makeStyles(() => ({
  paper: {
    width: "460px",
    maxWidth: "100vw",
    padding: "28px 32px 40px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "22px",
  },
  subtitle: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    marginBottom: "22px",
  },
  readonlyRow: { marginBottom: "18px" },
  readonlyLabel: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    opacity: 0.55,
    marginBottom: "4px",
  },
  readonlyValue: {
    fontSize: "15px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    borderRadius: "8px",
    padding: "10px 12px",
    wordBreak: "break-word",
  },
  lockNote: {
    fontSize: "11.5px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.55,
    marginTop: "3px",
  },
  actions: { display: "flex", gap: "12px", marginTop: "26px" },
}));

function ReadonlyField({ label, value, note }) {
  const classes = useStyles();
  return (
    <div className={classes.readonlyRow}>
      <div className={classes.readonlyLabel}>{label}</div>
      <div className={classes.readonlyValue}>{value || "—"}</div>
      {note ? <div className={classes.lockNote}>{note}</div> : null}
    </div>
  );
}

// Edit a Fortress bank-code resolution. For now ONLY bank_code is editable;
// bank_name and institution_code are shown read-only.
function BankCodeEditDrawer({
  open,
  resolution,
  loading = false,
  fieldErrors,
  onSubmit,
  onClose,
}) {
  const classes = useStyles();
  // Single form-object state (+ spread updates) rather than one useState per field.
  const [form, setForm] = useState({ bank_code: "" });
  const [clientError, setClientError] = useState("");

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (open) {
      setForm({ bank_code: resolution?.bank_code || "" });
      setClientError("");
    }
  }, [open, resolution]);

  const handleSubmit = () => {
    if (loading) return;
    const trimmed = (form.bank_code || "").trim();
    if (!trimmed) {
      setClientError("Bank code is required.");
      return;
    }
    onSubmit({ bank_code: trimmed });
  };

  const bankCodeError = clientError || serverFieldError(fieldErrors, "bank_code");
  const changed = (form.bank_code || "").trim() !== (resolution?.bank_code || "");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={loading ? undefined : onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.title}>Edit bank code</div>
        <IconButton size="small" onClick={onClose} disabled={loading} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.subtitle}>
        Update the bank code for this Fortress resolution.
      </div>

      <ReadonlyField
        label="Bank name"
        value={resolution?.bank_name}
        note="Not editable for now"
      />
      <ReadonlyField
        label="Institution code"
        value={resolution?.institution_code}
        note="Not editable for now"
      />

      <CommonInput
        label="Bank code"
        placeholder="e.g. 044"
        value={form.bank_code}
        onChange={(e) => {
          setField("bank_code", e.target.value);
          if (clientError) setClientError("");
        }}
        error={!!bankCodeError}
        helperText={bankCodeError}
      />

      <div className={classes.actions}>
        <CommonButton
          type="button"
          text="Cancel"
          stylevariant="secondary2"
          fontSize="15px"
          disabled={loading}
          onClick={onClose}
        />
        <CommonButton
          type="button"
          text="Save changes"
          stylevariant="primary"
          fontSize="15px"
          loading={loading}
          disabled={loading || !changed || !(form.bank_code || "").trim()}
          onClick={handleSubmit}
        />
      </div>
    </Drawer>
  );
}

export default BankCodeEditDrawer;
